/**
 * SmartCHG SSO callback landing page.
 *
 * Microsoft Entra redirects to `/callback?code=...&state=...` (or with
 * `error`/`error_description`). This component completes the token exchange
 * directly so the user sees one callback loader instead of returning through
 * the login screen.
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { API_CONFIG } from '../config/api';

// Kept for the legacy /login fallback path. /callback no longer writes this.
export const SSO_CALLBACK_STORAGE_KEY = 'smartchg_sso_callback';

type Status = 'loading' | 'error';

type BackendErrorPayload = {
    detail?: string;
    message?: string;
    error?: string;
};

export default function LoginCallback() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const { completeSSOLogin } = useAuth();
    const [status, setStatus] = useState<Status>('loading');
    const [errorMsg, setErrorMsg] = useState('');
    const calledRef = useRef(false);

    useEffect(() => {
        if (calledRef.current) return;
        calledRef.current = true;

        const body: Record<string, string> = {};
        const code = params.get('code');
        const state = params.get('state');
        const errParam = params.get('error');
        const errDesc = params.get('error_description');
        if (code) body.code = code;
        if (state) body.state = state;
        if (errParam) body.error = errParam;
        if (errDesc) body.error_description = errDesc;

        if (Object.keys(body).length === 0) {
            setStatus('error');
            setErrorMsg('No Microsoft sign-in response was received. Please try again.');
            return;
        }

        (async () => {
            try {
                const response = await fetch(`${API_CONFIG.BACKEND_BASE_URL}/auth/sso/callback`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(body),
                });

                if (!response.ok) {
                    let payload: BackendErrorPayload = {};
                    try {
                        payload = (await response.json()) as BackendErrorPayload;
                    } catch {
                        /* non-JSON body */
                    }
                    throw new Error(
                        payload.detail || payload.message || payload.error || `sign-in failed (${response.status})`
                    );
                }

                const data = await response.json();
                await completeSSOLogin(data);
                navigate('/', { replace: true });
            } catch (error: unknown) {
                setStatus('error');
                setErrorMsg(error instanceof Error ? error.message : 'Microsoft sign-in failed. Please try again.');
            }
        })();
    }, [completeSSOLogin, params, navigate]);

    return (
        <div
            style={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: 'Inter, system-ui, sans-serif',
                color: '#5F5F5F',
                fontSize: '0.9rem',
                gap: '0.75rem',
            }}
        >
            {status === 'loading' ? (
                <>
                    <div
                        style={{
                            width: '3rem',
                            height: '3rem',
                            border: '4px solid #e5e7eb',
                            borderTopColor: '#6E3450',
                            borderRadius: '9999px',
                            animation: 'spin 0.9s linear infinite',
                        }}
                    />
                    <p style={{ margin: 0, fontWeight: 600 }}>Completing Microsoft sign-in…</p>
                </>
            ) : (
                <>
                    <p style={{ margin: 0, color: '#B91C1C', fontWeight: 600 }}>Microsoft sign-in failed</p>
                    {errorMsg && (
                        <p style={{ margin: 0, maxWidth: 420, textAlign: 'center', fontSize: '0.85rem' }}>
                            {errorMsg}
                        </p>
                    )}
                    <button
                        type="button"
                        onClick={() => navigate('/login', { replace: true })}
                        style={{
                            marginTop: '0.25rem',
                            padding: '0.5rem 1rem',
                            borderRadius: '0.5rem',
                            border: '1px solid #d1d5db',
                            background: '#fff',
                            color: '#1F1A20',
                            cursor: 'pointer',
                        }}
                    >
                        Back to login
                    </button>
                </>
            )}
            <style>{'@keyframes spin { to { transform: rotate(360deg); } }'}</style>
        </div>
    );
}
